import { FC, useState } from 'react';
import { useMutation } from 'react-query';

import { ApiError } from 'model';
import { AppShellBaseProps, DetailsAppShell } from 'components/AppShell';
import { Content } from 'components/Page';
import { RoundButton } from 'components';
import { MyProfile } from 'MyProfile/model/MyProfile';
import { IsExternalType } from 'Dictionary/component';
import { myProfileApi } from 'MyProfile/service/myProfile.api';
import { AddInternalDto, mapUsersToDto } from 'MyProfile/model/AddInternalDto';
import { useToasts } from 'components/ToastProvider';
import { AddUserResponse } from 'MyProfile/model/AddUserResponse';
import { AddUsers } from 'MyProfile/component/AddUsers';
import { internalUsersRoute } from 'route';

export interface AddInternalUsersPageProps extends AppShellBaseProps {}

export const AddInternalUsersPage: FC<AddInternalUsersPageProps> = ({
  logout,
}) => {
  const { showError, showMany, showWarning } = useToasts();
  const [selectedUsers, setSelectedUsers] = useState<MyProfile[]>([]);
  const { mutate: addUsersToPcs, isLoading: addingUsersLoading } = useMutation<
    AddUserResponse,
    ApiError,
    AddInternalDto[]
  >((usersToAdd) => myProfileApi.addInternals(usersToAdd), {
    onSuccess: (response) => {
      if (response.failed.length && !response.success.length) {
        showMany("Action failed for all users", response.failed.map(response => response.message), true);
      }
      if (!response.failed.length && response.success.length) {
        showMany("Users were added successfully", response.success.map(response => response.userEmail), false);        
        setSelectedUsers([]);
      }
      if (response.failed.length && response.success.length) {
        const addedUserEmails: string[] = response.success.map(
          (userResponse) => userResponse.userEmail
        );
        showWarning('Some users were not added to PCS2');
        showMany("Added users", response.success.map(response => response.userEmail), false);
        showMany("Failed users", response.failed.map(response => response.message), true);        
        setSelectedUsers((prevState) =>
          prevState.filter((user) => !addedUserEmails.includes(user.email))
        );
      }
    },
    onError: () => showError('Add users to PCS2 action failed'),
  });
  const handleOnAddUsers = () => addUsersToPcs(mapUsersToDto(selectedUsers));
  const handleSetSelectedUsers = (users: MyProfile[]) =>
    setSelectedUsers(users);
  return (
    <DetailsAppShell
      title="Add internal user"
      subtitle="Internal users list"
      logout={logout}
      defaultGoBackPath={internalUsersRoute.path}
    >
      <Content
        mutating={addingUsersLoading}
        mutatingLabel="Adding users to PCS2..."
        title="Add internal user"
        subtitle="You can add to the PCS2 any user who is already ABB employee"
        renderActions={() => (
          <RoundButton
            color="secondary"
            variant="contained"
            disabled={!selectedUsers.length}
            onClick={handleOnAddUsers}
          >
            Add users to PCS2
          </RoundButton>
        )}
      >
        <AddUsers
          isExternalType={IsExternalType.Abb}
          selectedUsers={selectedUsers}
          setSelectedUsers={handleSetSelectedUsers}
        />
      </Content>
    </DetailsAppShell>
  );
};
